'use client'

import { Button } from '@mentora/ui-components'
import { ImageIcon, Loader2, Trash2, Upload } from 'lucide-react'
import { useTranslations } from 'next-intl'
import { useRouter } from 'next/navigation'
import { useRef, useState, useTransition } from 'react'
import { toast } from 'sonner'

interface Props {
  courseId: string
  thumbnailUrl: string | null
}

export function CourseThumbnailUpload({ courseId, thumbnailUrl }: Props) {
  const router = useRouter()
  const t = useTranslations('courses.editor')
  const tErrors = useTranslations('errors')
  const inputRef = useRef<HTMLInputElement>(null)
  const [uploading, setUploading] = useState(false)
  const [removing, startRemoving] = useTransition()
  const [preview, setPreview] = useState<string | null>(null)

  const displayed = preview ?? thumbnailUrl

  async function handleFile(file: File) {
    setUploading(true)
    const objectUrl = URL.createObjectURL(file)
    setPreview(objectUrl)

    const formData = new FormData()
    formData.append('file', file)
    formData.append('courseId', courseId)

    try {
      const res = await fetch('/api/upload/course-thumbnail', {
        method: 'POST',
        body: formData,
      })

      if (!res.ok) {
        const data = (await res.json().catch(() => ({}))) as { error?: string }
        toast.error(data.error ? tErrors(data.error) : t('thumbnailUploadFailed'))
        setPreview(null)
        return
      }

      toast.success(t('thumbnailUpdated'))
      router.refresh()
    }
    catch {
      toast.error(t('thumbnailUploadFailed'))
      setPreview(null)
    }
    finally {
      setUploading(false)
      URL.revokeObjectURL(objectUrl)
    }
  }

  function handleRemove() {
    startRemoving(async () => {
      const res = await fetch('/api/upload/course-thumbnail', {
        method: 'DELETE',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ courseId }),
      })

      if (!res.ok) {
        const data = (await res.json().catch(() => ({}))) as { error?: string }
        toast.error(data.error ? tErrors(data.error) : t('thumbnailRemoveFailed'))
        return
      }

      setPreview(null)
      toast.success(t('thumbnailRemoved'))
      router.refresh()
    })
  }

  const busy = uploading || removing

  return (
    <div className="flex flex-col gap-3">
      <div className="relative aspect-video w-full max-w-md overflow-hidden rounded-lg border border-border/60 bg-[var(--color-muted)]/30">
        {displayed
          ? (
              <img
                src={displayed}
                alt={t('thumbnail')}
                className="h-full w-full object-cover"
              />
            )
          : (
              <div className="flex h-full w-full flex-col items-center justify-center gap-2 text-muted-foreground">
                <ImageIcon className="size-8" />
                <p className="text-sm">{t('thumbnailEmpty')}</p>
              </div>
            )}

        {busy && (
          <div className="absolute inset-0 flex items-center justify-center bg-overlay/40">
            <Loader2 className="size-6 animate-spin text-overlay-foreground" />
          </div>
        )}
      </div>

      <div className="flex flex-wrap gap-2">
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => inputRef.current?.click()}
          disabled={busy}
        >
          <Upload className="size-4" />
          {thumbnailUrl ? t('thumbnailReplace') : t('thumbnailUpload')}
        </Button>
        {thumbnailUrl && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={handleRemove}
            disabled={busy}
          >
            <Trash2 className="size-4" />
            {t('thumbnailRemove')}
          </Button>
        )}
      </div>

      <p className="text-xs text-muted-foreground">{t('thumbnailHint')}</p>

      <input
        ref={inputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp,image/gif"
        className="hidden"
        onChange={(e) => {
          const file = e.target.files?.[0]
          if (file)
            handleFile(file)
          e.target.value = ''
        }}
      />
    </div>
  )
}
